import React, { useState } from 'react';
import { Spinner } from '@/components/primitives/spinner';
import { FeedCommentDto } from '@/api/comment';
import { formatRelativeTime } from '@/utils/date';
import Typography from '@/components/primitives/typography';
import styles from './styles.module.scss';
import Avatar from '@/components/primitives/avatar';
import Input from '@/components/primitives/input';
import CaptionInput from '@/screen/feed/write/components/caption-input';
import Button from '@/components/primitives/button';
import Caption from '@/components/complex/caption';
import { useAuth } from '@/context/auth-context';
import { Flag, MoreVertical, PenSquare, Trash2 } from 'lucide-react';
import { DropDown, DropDownItem } from '@/components/primitives/dropdown';
import { router } from 'next/client';
import Link from 'next/link';

interface CommentItemProps {
  comment: FeedCommentDto;
  editingComment: FeedCommentDto | null;
  isCommentSubmitting: boolean;
  onEdit: (comment: FeedCommentDto) => void;
  onDelete: (commentId: number) => void;
  onCancelEdit: () => void;
  onUpdateComment: () => void;
  onChangeEditingComment: (text: string) => void;
}

/**
 * Component for displaying a single comment
 */
const CommentItem: React.FC<CommentItemProps> = ({
                                                   comment,
                                                   editingComment,
                                                   isCommentSubmitting,
                                                   onEdit,
                                                   onDelete,
                                                   onCancelEdit,
                                                   onUpdateComment,
                                                   onChangeEditingComment,
                                                 }) => {
  const auth = useAuth();
  const isEditing = editingComment?.id === comment.id;
  // Check if current user is the owner of the feed
  const isOwner = auth.me?.activeAccount?.name === comment.account?.name;
  const [showMenu, setShowMenu] = useState(false);

  // 키보드 이벤트 핸들러: Enter로 수정 완료, Shift+Enter로 개행
  const handleKeyPress = (e: React.KeyboardEvent) => {
    const webEvent = e.nativeEvent as any;
    if (webEvent.key === 'Enter' && !webEvent.shiftKey) {
      e.preventDefault();
      if (editingComment?.content?.trim() && !isCommentSubmitting) {
        onUpdateComment();
      }
    }
  };

  const handleClickMenu = () => {
    setShowMenu(true);
    setTimeout(() => {
      const handleClickWindow = () => {
        setShowMenu(false);
        window.removeEventListener('click', handleClickWindow);
      };

      window.addEventListener('click', handleClickWindow);
    }, 50);
  };

  return (
    <div key={comment.id} className={styles.commentContainer}>
      <Link href={`/accounts/${comment.account?.name}`}>
        <Avatar account={comment.account} variant="w96" />
      </Link>
      <div className={styles.contentContainer}>
        {/* Comment header */}
        <Link href={`/accounts/${comment.account?.name}`}>
          <div className={styles.userName}>
            {!!comment.account?.nickname && <Typography size="small" weight='regular'>{comment.account?.nickname || ''}</Typography>}
            <Typography className="color-gray-600" size="small">@{comment.account?.name}</Typography>
          </div>
        </Link>
        {/* Comment content or edit form */}
        {isEditing ? (
          <div className={styles.editForm}>
            <CaptionInput
              label=""
              className={styles.editInput}
              caption={editingComment?.content || ''}
              onChange={(e) => onChangeEditingComment?.call(null, e.target.value)}
              onKeyDown={handleKeyPress}
            />
            <div className={styles.buttonContainer}>
              <Button
                onClick={onCancelEdit}
                variant="secondary"
              >
                <Typography>취소</Typography>
              </Button>
              <Button
                aurora={isCommentSubmitting}
                onClick={() => {
                  if (isCommentSubmitting) return;
                  onUpdateComment.call(null);
                }}
              >
                <Typography className={styles.saveButtonText}>수정</Typography>
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div>
              <Caption text={comment.content} />
            </div>
            {comment.createdAt && (
              <Typography className="color-gray-500" size="small">
                {formatRelativeTime(comment.createdAt)}
              </Typography>
            )}
          </>
        )}
      </div>

      {/* Comment actions */}
      {!isEditing && !!isOwner && (
        <div
          className={`${styles.more} cursor-pointer`}
          onClick={handleClickMenu}
        >
          <MoreVertical
            width={24}
            height={24}
            strokeWidth={1.5}
          />
          <div className={styles.dropdownContainer}>
            {/* BottomSheet for feed options */}
            <DropDown open={showMenu} stickyPosition="right">
              <DropDownItem className={styles.menuItemContainer} onSelect={() => onEdit(comment)}>
                <PenSquare width={20} height={20} strokeWidth={1.5} />
                <Typography className={styles.text} size="small">수정</Typography>
              </DropDownItem>
              <DropDownItem className={styles.menuItemContainer} onSelect={() => {
                if (isCommentSubmitting) return;
                onDelete(comment.id!);
              }}>
                <Trash2 width={20} height={20} strokeWidth={1.5} color="#FF4D4F" />
                <Typography className={`${styles.bottomSheetDangerText} ${styles.text}`}
                            size="small">삭제</Typography>
              </DropDownItem>
            </DropDown>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommentItem;
